import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useId } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Le libellé d'un champ, et son astérisque s'il est obligatoire.
 *
 * L'astérisque est DEHORS de l'élément `<label>`, et ce n'est pas un détail de
 * mise en page : placé dedans, il entre dans le NOM ACCESSIBLE du champ, que
 * `aria-hidden` ne suffit pas à en retirer. Le champ s'appelait alors « Nom * »
 * — annoncé « Nom étoile » par un lecteur d'écran, et introuvable par son vrai
 * nom. Constaté le 12 septembre 2026 : le parcours de bout en bout ne trouvait
 * plus le champ « Nom », ce qui était la bonne nouvelle de la journée.
 *
 * L'astérisque est donc une décoration purement visuelle ; l'information, elle,
 * est portée par l'attribut `required` de l'élément — que les lecteurs d'écran
 * annoncent — et par la légende `MentionObligatoire`, qui dit en toutes lettres
 * ce que le symbole signifie.
 */
function Libelle({
  pour,
  children,
  requis,
}: {
  pour: string;
  children: ReactNode;
  requis?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-0.5">
      <label htmlFor={pour} className="text-sm font-medium text-encre">
        {children}
      </label>
      {requis ? (
        <span aria-hidden className="text-alerte">
          *
        </span>
      ) : null}
    </div>
  );
}

export function MentionObligatoire({ className }: { className?: string }) {
  return (
    <p className={cn("text-xs text-encre-douce", className)}>
      Les champs marqués <span className="text-alerte">*</span> sont obligatoires.
    </p>
  );
}

type Props = InputHTMLAttributes<HTMLInputElement> & {
  libelle: string;
  aide?: string;
};

export function Champ({ libelle, aide, className, id, ...reste }: Props) {
  const genere = useId();
  const identifiant = id ?? genere;
  const idAide = aide ? `${identifiant}-aide` : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <Libelle pour={identifiant} requis={reste.required}>
        {libelle}
      </Libelle>
      <input
        id={identifiant}
        aria-describedby={idAide}
        className={cn(
          "h-11 rounded-[6px] border border-filet bg-white px-3 text-sm text-encre",
          "placeholder:text-encre-douce/70",
          className,
        )}
        {...reste}
      />
      {aide ? (
        <p id={idAide} className="text-xs text-encre-douce">
          {aide}
        </p>
      ) : null}
    </div>
  );
}

type PropsSelection = SelectHTMLAttributes<HTMLSelectElement> & {
  libelle: string;
  aide?: string;
  options: { valeur: string; libelle: string }[];
};

export function ChampSelection({ libelle, aide, options, className, id, ...reste }: PropsSelection) {
  const genere = useId();
  const identifiant = id ?? genere;
  const idAide = aide ? `${identifiant}-aide` : undefined;
  // Un `select` prend la largeur de sa plus longue option : sans `min-w-0`, un
  // nom de fournisseur long fait déborder l'écran du téléphone.
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Libelle pour={identifiant} requis={reste.required}>
        {libelle}
      </Libelle>
      <select
        id={identifiant}
        aria-describedby={idAide}
        className={cn(
          "h-11 min-w-0 max-w-full rounded-[6px] border border-filet bg-white px-3 text-sm",
          "text-encre",
          className,
        )}
        {...reste}
      >
        {options.map((option) => (
          <option key={option.valeur} value={option.valeur}>
            {option.libelle}
          </option>
        ))}
      </select>
      {aide ? (
        <p id={idAide} className="text-xs text-encre-douce">
          {aide}
        </p>
      ) : null}
    </div>
  );
}

type PropsTexte = TextareaHTMLAttributes<HTMLTextAreaElement> & { libelle: string };

export function ChampTexte({ libelle, className, id, ...reste }: PropsTexte) {
  const genere = useId();
  const identifiant = id ?? genere;
  return (
    <div className="flex flex-col gap-1.5">
      <Libelle pour={identifiant} requis={reste.required}>
        {libelle}
      </Libelle>
      <textarea
        id={identifiant}
        rows={3}
        className={cn(
          "rounded-[6px] border border-filet bg-white px-3 py-2 text-sm text-encre",
          className,
        )}
        {...reste}
      />
    </div>
  );
}

type PropsPuces = {
  libelle: string;
  options: { valeur: string; libelle: string }[];
  valeur: string;
  onChanger: (valeur: string) => void;
  required?: boolean;
};

/**
 * Un choix parmi deux ou trois — en puces cliquables plutôt qu'en menu déroulant.
 *
 * Deux options ne méritent pas un menu : il faut deux gestes pour l'ouvrir et
 * choisir, là où une puce en demande un. En dessous de quatre ou cinq choix,
 * tout montrer est plus rapide à lire ET à toucher — les cibles font 44 px, le
 * terrain travaille avec des gants.
 *
 * Ce sont de VRAIS boutons radio, visuellement masqués : le groupe s'annonce au
 * lecteur d'écran, les flèches du clavier le parcourent, et le formulaire les
 * envoie. Reconstruire tout cela avec des `<div>` et des gestionnaires de clic
 * aurait coûté plus cher et moins bien marché.
 *
 * ADR-0024 : la puce choisie ne se distingue pas par la seule couleur — elle
 * porte une COCHE. Imprimée en noir et blanc, la page ne perd rien.
 */
export function ChampPuces({ libelle, options, valeur, onChanger, required }: PropsPuces) {
  const nom = useId();
  return (
    <fieldset className="flex min-w-0 flex-col gap-1.5 border-0 p-0">
      <legend className="text-sm font-medium text-encre">
        {libelle}
        {required ? (
          <span aria-hidden className="ml-0.5 text-alerte">
            *
          </span>
        ) : null}
      </legend>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const choisie = valeur === option.valeur;
          return (
            <label key={option.valeur} className="cursor-pointer">
              <input
                type="radio"
                name={nom}
                value={option.valeur}
                checked={choisie}
                required={required}
                onChange={() => onChanger(option.valeur)}
                className="peer sr-only"
              />
              <span
                className={cn(
                  "inline-flex h-11 items-center gap-1.5 rounded-[6px] border px-4 text-sm",
                  "peer-focus-visible:ring-2 peer-focus-visible:ring-laiton-fonce",
                  "peer-focus-visible:ring-offset-2",
                  choisie
                    ? "border-laiton bg-laiton font-medium text-white"
                    : "border-filet bg-white text-encre hover:bg-chene-clair/40",
                )}
              >
                <Check
                  className={cn("h-3.5 w-3.5 shrink-0", choisie ? "opacity-100" : "opacity-0")}
                  aria-hidden
                />
                {option.libelle}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
